import { useCallback } from 'react';
import { throttle } from 'throttle-debounce';

import { useFullscreen } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Layout/hooks/useFullscreen';
import { useMute } from '@/scenarios/legacy-player/hooks/useMute';
import { useVolume } from '@/scenarios/legacy-player/hooks/useVolume';

import { KeyboardKeys, REPEATABLE_KEY_CODES } from '../../constants/keyboard';
import { VOLUME_STEP } from '../../constants/skin';
import { isKeyboardKeyCode } from '../../types/keyboard-keys';
import { soundPreferencesMute } from '../config/use-sound-preferences';

import { useRestrictedSeekWithIndicator } from './use-restricted-seek-with-indicator';
import { useIsGlobalKeyboardHandlerTurnedOnChecker } from './use-is-global-keyboard-handler-turned-on-checker';
import { useTogglePlayWithIndicator } from './use-toggle-play-with-indicator';
import { useToggleOnAirWithIndicator } from './use-toggle-on-air-with-indicator';

interface Params {
    onClose?: () => void;
    isChannel?: boolean;
}

export const useOnKeyDown = ({ onClose, isChannel }: Params) => {
    const { togglePlay } = useTogglePlayWithIndicator();
    const { toggleOnAir } = useToggleOnAirWithIndicator();
    const { seekBackward, seekForward } = useRestrictedSeekWithIndicator();
    const { changeVolumeByStep } = useVolume();
    const { toggleFullscreen } = useFullscreen();
    const { isMuted, toggleMute } = useMute();

    const toogleMuteWithSave = () => {
        toggleMute();
        soundPreferencesMute.set(!isMuted);
    };

    const checkIsKeyboardHandlerTurnedOn = useIsGlobalKeyboardHandlerTurnedOnChecker();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback(
        throttle(500, true, (event: KeyboardEvent) => {
            const {
                Space,
                Escape,
                ArrowDown,
                ArrowLeft,
                ArrowRight,
                ArrowUp,
                KeyF,
                KeyJ,
                KeyK,
                KeyL,
                KeyM,
            } = KeyboardKeys;

            const { code, repeat } = event;

            if (!isKeyboardKeyCode(code)) {
                return;
            }

            if (!checkIsKeyboardHandlerTurnedOn(code)) {
                return;
            }

            if (repeat && !REPEATABLE_KEY_CODES.includes(code)) {
                return;
            }

            switch (event.code) {
                case Space:
                case KeyK: {
                    if (isChannel) {
                        toggleOnAir();
                    } else {
                        togglePlay();
                    }

                    break;
                }
                case ArrowRight:
                case KeyL: {
                    seekForward();
                    break;
                }
                case ArrowLeft:
                case KeyJ: {
                    seekBackward();
                    break;
                }
                case ArrowUp: {
                    changeVolumeByStep(VOLUME_STEP);
                    break;
                }
                case ArrowDown: {
                    changeVolumeByStep(-VOLUME_STEP);
                    break;
                }
                case KeyF: {
                    if (!(event.metaKey && event.ctrlKey)) {
                        toggleFullscreen();
                    }
                    break;
                }
                case KeyM: {
                    toogleMuteWithSave();
                    break;
                }
                case Escape: {
                    onClose?.();
                    break;
                }
                default: {
                    break;
                }
            }
        }),
        [
            onClose,
            seekBackward,
            seekForward,
            toggleFullscreen,
            toggleMute,
            togglePlay,
            changeVolumeByStep,
            checkIsKeyboardHandlerTurnedOn,
        ]
    );
};
